
import React, { useState, useEffect } from "react";
import {
  Button, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Box, Typography, IconButton,
  TableCell, TableContainer, Paper, Table, TableBody, TableHead, TableRow, Tooltip
} from '@mui/material';
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import styled, { keyframes, css } from 'styled-components';
import '../../styles/JDDetails.css';
import '../../styles/HiringdashBoard.css';
import JobDescriptionForm from "../HiringManager/JobDescriptionForm";
import Pagination from "../../utils/Pagination";
import CandidateData from './CandidateData.js';
import ScreeningTestDetails from "./ScreeningTestDetails.js";
import NotificationDialog from './NotificationDialog';
import { ThemeProvider } from '@mui/material/styles';
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, ELLIPSIS } from '../../styles/recruiterTheme';

// MUI icons
import WorkIcon from '@mui/icons-material/Work';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SwapVertIcon from '@mui/icons-material/SwapVert';

// Single-line truncation (CSS form of the ELLIPSIS token).
const ELLIPSIS_CSS = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// Horizontal page gutter shared by every section so all blocks line up.
const GUTTER = 32;

// ─── Page Wrapper ─────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  font-family: ${FONT_BODY};
  color: ${COLORS.ink};
  background: #F3EEF9;
  min-height: 100vh;
  padding-bottom: 40px;
  width: 100%;
`;

// ─── Header ───────────────────────────────────────────────────────────────────
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 110px ${GUTTER}px 0 ${GUTTER}px;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeaderSubtitle = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  line-height: 1.5;
  color: ${COLORS.muted};
  margin-top: 4px;
  ${ELLIPSIS_CSS}
`;

const BackBtn = styled(IconButton)`
  && {
    background: linear-gradient(135deg, #F3EEF9, #E8DEF8);
    border-radius: ${RADIUS.sm}px;
    width: 36px; height: 36px;
    flex-shrink: 0;
    transition: background 0.2s;
    &:hover {
      background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
    }
    svg { color: #4F378B; font-size: 20px; }
  }
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const JdIdChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  background: #EDE7F6;
  color: #4F378B;
  border-radius: ${RADIUS.sm}px;
  padding: 0 12px;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
`;

const HeaderStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  background: ${p => (p.active ? '#E6F4EA' : '#FDECEA')};
  color: ${p => (p.active ? '#2D7D46' : '#B3261E')};
`;

// ─── Date Pills (right side of header) ───────────────────────────────────────
const DatePillRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const DatePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  background: #fff;
  border: 1.5px solid #EDE7F6;
  border-radius: ${RADIUS.pill}px;
  padding: 0 12px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  color: #4F378B;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 6px rgba(79,55,139,0.07);
`;

// ─── Stat Cards ───────────────────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  padding: 0 ${GUTTER}px;
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg,#6750A4,#4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  padding-right: 56px;
  min-height: 88px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 18px ${p => p.shadow || 'rgba(103,80,164,0.26)'};
  transition: transform 0.2s, box-shadow 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 24px ${p => p.shadow || 'rgba(103,80,164,0.36)'}; }
  &::after {
    content: '';
    position: absolute;
    top: -24px; right: -24px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
  max-width: 100%;
  ${ELLIPSIS_CSS}
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const StatIconWrap = styled.div`
  position: absolute;
  top: 50%; right: 16px;
  transform: translateY(-50%);
  display: flex;
  opacity: 0.2;
  svg { font-size: 32px; }
`;

// ─── Content Grid ─────────────────────────────────────────────────────────────
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 0 ${GUTTER}px;
  margin-bottom: 32px;
  animation: ${fadeSlideIn} 0.45s ease 0.05s both;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const InfoCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.lg}px !important;
    padding: 24px !important;
    box-shadow: 0 3px 18px rgba(79,55,139,0.08) !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
    height: 100%;
    box-sizing: border-box;
    min-width: 0;
  }
`;

const CardTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-bottom: 16px !important;
    padding-bottom: 12px !important;
    border-bottom: 2px solid #F0EAF9;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

// Label / value grid: fixed label column so every value starts on the same x.
const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 152px minmax(0, 1fr);
  column-gap: 16px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid #FAF7FF;
  &:last-child { border-bottom: none; }
  @media (max-width: 480px) { grid-template-columns: 1fr; row-gap: 4px; }
`;

const InfoKey = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.5;
  color: #8B7DB5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const InfoVal = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  color: #2D1B5E;
  line-height: 1.5;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ContactEmail = styled.span`
  display: block;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  line-height: 1.5;
  color: #4F378B;
  ${ELLIPSIS_CSS}
`;

// ─── Skills Chips ─────────────────────────────────────────────────────────────
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  background: ${p => p.secondary ? '#FFF3E0' : '#EDE7F6'};
  color: ${p => p.secondary ? '#E65100' : '#4F378B'};
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  padding: 0 8px;
  border-radius: ${RADIUS.sm}px;
  border: 1px solid ${p => p.secondary ? '#FFB74D' : '#C4B0E8'};
  white-space: nowrap;
`;

const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  background: #E6F4EA;
  color: #2D7D46;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
`;

// ─── JD Description Box ───────────────────────────────────────────────────────
const DescBox = styled(Paper)`
  && {
    border-radius: ${RADIUS.lg}px !important;
    padding: 24px !important;
    box-shadow: 0 3px 18px rgba(79,55,139,0.08) !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
    margin: 0 ${GUTTER}px 24px ${GUTTER}px;
    animation: ${fadeSlideIn} 0.5s ease 0.1s both;
  }
`;

const DescText = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    font-weight: 400 !important;
    color: #3D2B6B;
    line-height: 1.6 !important;
    max-width: 80ch;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
`;

// ─── Table ────────────────────────────────────────────────────────────────────
const TableWrap = styled.div`
  padding: 0 ${GUTTER}px;
  margin-bottom: 32px;
  animation: ${fadeSlideIn} 0.5s ease 0.15s both;
`;

const TableCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    overflow: hidden;
    box-shadow: 0 3px 20px rgba(79,55,139,0.09) !important;
    border: 1px solid #EDE7F6;
  }
`;

const SectionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.section}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-top: 0 !important;
    margin-bottom: 16px !important;
    display: flex;
    align-items: center;
    gap: 8px;
    &::before {
      content: '';
      width: 4px;
      height: 20px;
      border-radius: 2px;
      background: linear-gradient(180deg, #6750A4, #4F378B);
      flex-shrink: 0;
    }
  }
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  background: #EDE7F6;
  color: #4F378B;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.15s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: #3D2B6B !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #F0EAF9 !important;
    vertical-align: middle;
  }
`;

const CandidateIdBtn = styled(Button)`
  && {
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    text-decoration: underline !important;
    text-underline-offset: 3px !important;
    min-width: 0;
    padding: 4px 8px !important;
    border-radius: ${RADIUS.sm}px !important;
    &:hover { background: rgba(103,80,164,0.07) !important; }
  }
`;

// ─── Status Chip ──────────────────────────────────────────────────────────────
const rejectedStatuses = [
  'ScreeningTest_Failed', 'Recruiter_Rejected', 'ReScreeningTest_Failed',
  'HiringManager_Rejected', 'Rejected', 'Candidate_Declined', 'Candidate_Quit',
  'ReTestPassed_Rejected', 'TestPassed_Rejected'
];
const passedStatuses = ['Selected', 'ScreeningTest_Passed', 'ReScreeningTest_Passed', 'Round2_Suggested'];

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  white-space: nowrap;
  background: ${p => p.bg};
  color: ${p => p.fg};
`;

const StatusDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.color};
  display: inline-block;
  flex-shrink: 0;
`;

const getStatusChip = (status) => {
  if (!status) return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
  if (status === 'NewCandidate') return { bg: '#FFF8E1', fg: '#E65100', dot: '#FFB300' };
  if (rejectedStatuses.includes(status)) return { bg: '#FDECEA', fg: '#B3261E', dot: '#E53935' };
  if (passedStatuses.includes(status)) return { bg: '#E6F4EA', fg: '#2D7D46', dot: '#2D7D46' };
  return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
};

// ─── New badge ────────────────────────────────────────────────────────────────
const NewBadge = styled.span`
  position: absolute;
  top: -8px; right: -24px;
  background: #FFD600;
  color: #5D4037;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.25;
  border-radius: 4px;
  padding: 0 4px;
  letter-spacing: 0.06em;
`;

// ─── Empty / loading / error state ───────────────────────────────────────────
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  padding: 40px 24px;
  background: #fff;
  border-radius: ${RADIUS.md}px;
  border: 1px solid #EDE7F6;
`;

const emptyTitleSx = { fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, fontWeight: 700, lineHeight: 1.25 };
const emptyTextSx = { fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: '#8B7DB5' };
const outlineBtnSx = {
  fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: '#6750A4',
  border: '1.5px solid #C4B0E8', borderRadius: `${RADIUS.sm}px`, height: 36, px: 2,
};

// ─── Dialog styles ────────────────────────────────────────────────────────────
const DialogTitleStyled = styled(DialogTitle)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.section}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const JDDetailsContent = ({ hiringManagerEmail, jdDetails: jdDetailsProp, userRole: userRoleProp, salaryRange, companyName, recruiterEmail, readOnly, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Direct /JDDetails route renders without props — fall back to router state.
  const jdDetails = jdDetailsProp || location.state?.jdDetails || location.state?.jd || null;
  const userRole = userRoleProp || location.state?.userRole;
  const handleBack = onBack || (() => navigate(-1));
  const [jds, setJds] = useState([]);
  const [selectedJD, setSelectedJD] = useState(null);
  const [isEditJDOpen, setIsEditJDOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [candidateProfiles, setCandidateProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recruiterEmails, setRecruiterEmails] = useState({});
  const [interviewerEmails, setInterviewerEmails] = useState({});
  const [actionTrigger, setActionTrigger] = useState(false);
  const [screeningIDs, setScreeningIDs] = useState(() => {
    try {
      const stored = localStorage.getItem('screeningIDs');
      return stored ? JSON.parse(stored) || {} : {};
    } catch {
      return {};
    }
  });

  const extractjobdetailsApiUrl = process.env.REACT_APP_EXTRACT_JOB_DETAILS_API;
  const getRecruitersAndInterviewersApiUrl = process.env.REACT_APP_GET_RECRUITERS_INTERVIEWERS_API;

  const [summaries, setSummaries] = useState([]);
  const [loadingSearchCandidatesMap, setLoadingSearchCandidatesMap] = useState({});
  const [isViewJdDetailsOpen, setIsViewJdDetailsOpen] = useState(false);
  const [resumeData, setResumeData] = useState(null);

  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCandidateData, setShowCandidateData] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState("");
  const [overallStatus, setOverAllStatus] = useState('');
  const [showScreeningDetails, setShowScreeningDetails] = useState(false);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(candidateProfiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  // Reset pagination when a different JD is shown.
  useEffect(() => {
    setCurrentPage(1);
  }, [jdDetails?._id]);

  useEffect(() => {
    if (jdDetails && jdDetails._id) fetchCandidateProfiles(jdDetails._id);
  }, [jdDetails, actionTrigger]);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const response = await axios.get(getRecruitersAndInterviewersApiUrl);
        const { recruiters, interviewers } = response.data;
        const recruiterMap = {};
        recruiters.forEach((r) => { recruiterMap[r._id] = r.email; });
        const interviewerMap = {};
        interviewers.forEach((i) => { interviewerMap[i._id] = i.email; });
        setRecruiterEmails(recruiterMap);
        setInterviewerEmails(interviewerMap);
      } catch (error) { console.error("Error fetching contacts:", error); }
    };
    fetchContacts();
  }, []);

  const fetchCandidateProfiles = async (jdId) => {
    setProfilesLoading(true);
    setProfilesError('');
    try {
      const response = await axios.get(`${process.env.REACT_APP_CANDIDATE_PROFILE_BY_JDID_API}/${jdId}`);
      setCandidateProfiles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status === 404) setCandidateProfiles([]);
      else {
        console.error('Failed to fetch profiles:', error);
        setCandidateProfiles([]);
        setProfilesError(error.response?.data?.message || 'Failed to load candidate profiles. Please try again.');
      }
    } finally {
      setProfilesLoading(false);
    }
  };

  const fetchJds = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_FETCH_JDS_API}=${hiringManagerEmail}`);
      setJds(response.data.activeJds);
    } catch (error) { console.error('Error fetching JDs:', error); }
  };

  const handleDelete = async (jdId) => {
    try {
      await axios.put(`${process.env.REACT_APP_DELETE_JD_API}/${jdId}`);
      setSnackbarMessage('Job description deleted successfully');
      setSnackbarOpen(true);
      navigate("/hiring-dashboard", { state: { hiringManagerEmail } });
    } catch (error) { console.error('Failed to delete JD:', error); }
    setOpenDeleteDialog(false);
  };

  const handlebuttonclick1 = (candidateId, candidateEmail, overallStatus) => {
    if (!candidateId) return;
    setSelectedCandidateId(candidateId);
    setCandidateEmail(candidateEmail);
    setOverAllStatus(overallStatus);
    setIsModalOpen(true);
    setShowCandidateData(true);
    setActionTrigger((prev) => !prev);
  };

  const handleClose = () => {
    setSelectedCandidateId(null);
    setShowCandidateData(false);
    setActionTrigger((prev) => !prev);
  };

  const handleSearchCandidates = async (jd) => {
    setLoadingSearchCandidatesMap(prev => ({ ...prev, [jd._id]: true }));
    try {
      const requestData = { job_description: jd.jobDescription, job_role: jd.jobRole, skills: jd.skills, experience: jd.experience, jddetails_id: jd._id, jdID: jd.jdID };
      const response = await axios.post(extractjobdetailsApiUrl, requestData);
      const documentSummaries = response.data.document_summaries;
      for (const documentSummary of documentSummaries) {
        let resumeData = {};
        if (documentSummary?.content?.metadata) {
          resumeData = { resumeUrl: documentSummary.content.metadata.source, extracted_email: documentSummary.content.extracted_email, extracted_phone_number: documentSummary.content.extracted_phone_number, jobDetailsId: jd._id, jdID: jd.jdID, extracted_name: documentSummary.content.extracted_name, match_score: documentSummary.Match_Score, summaries: documentSummary.summary, job_role: jd.jobRole };
        } else if (documentSummary) {
          resumeData = { resumeUrl: documentSummary.Resume, extracted_email: documentSummary.extracted_email, extracted_phone_number: documentSummary.extracted_phone_number, jobDetailsId: jd._id, jdID: jd.jdID, extracted_name: documentSummary.extracted_name, match_score: documentSummary.Match_Score, summaries: documentSummary.Summary, job_role: jd.jobRole };
        }
      }
      setResumeData({ jdId: jd._id, summaries });
      setIsViewJdDetailsOpen(true);
      setSelectedJD({ ...jd, userRole: 'recruiter' });
      setSnackbarMessage('Search completed successfully.');
      setSnackbarOpen(true);
    } catch (error) { setSnackbarMessage("Failed to fetch resumes."); setSnackbarOpen(true); }
    finally { setLoadingSearchCandidatesMap(prev => ({ ...prev, [jd._id]: false })); }
  };

  if (!jdDetails) return (
    <PageWrapper>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, height: '60vh', textAlign: 'center', px: 3 }}>
        <Typography sx={{ ...emptyTitleSx, fontSize: TYPE.section, color: '#2D1B5E' }}>JD not found</Typography>
        <Typography sx={emptyTextSx}>
          This job description could not be loaded.
        </Typography>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={outlineBtnSx}>
          Back
        </Button>
      </Box>
    </PageWrapper>
  );

  const paginatedProfiles = candidateProfiles.slice(startIndex, startIndex + itemsPerPage);

  // Computed stats from candidates
  const totalCandidates = candidateProfiles.length;
  const passedCount = candidateProfiles.filter(p => passedStatuses.includes(p.overallStatus)).length;
  const rejectedCount = candidateProfiles.filter(p => rejectedStatuses.includes(p.overallStatus)).length;
  const newCount = candidateProfiles.filter(p => p.overallStatus === 'NewCandidate').length;
  const avgScore = totalCandidates > 0
    ? Math.round(candidateProfiles.reduce((s, p) => s + (p.match_score || 0), 0) / totalCandidates)
    : 0;

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <PageWrapper>
      {showScreeningDetails ? (
        <ScreeningTestDetails screeningID={screeningIDs[jdDetails._id]} />
      ) : !isEditJDOpen && !showCandidateData ? (
        <>
          {/* Header */}
          <HeaderBar>
            <HeaderLeft>
              <BackBtn onClick={handleBack}><ArrowBackIcon /></BackBtn>
              <Box sx={{ minWidth: 0 }}>
                <HeaderTitleRow>
                  <PageTitle>Job Description</PageTitle>
                  <JdIdChip>{jdDetails.jdID}</JdIdChip>
                  <HeaderStatusPill active={jdDetails.is_active ? 1 : 0}>
                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                    {jdDetails.is_active ? 'Active' : 'Inactive'}
                  </HeaderStatusPill>
                </HeaderTitleRow>
                <HeaderSubtitle title={`${jdDetails.jobRole || ''} ${jdDetails.companyName ? `· ${jdDetails.companyName}` : ''}`}>
                  {jdDetails.jobRole} {jdDetails.companyName ? `· ${jdDetails.companyName}` : ''}
                </HeaderSubtitle>
              </Box>
            </HeaderLeft>

            {/* Date pills */}
            <DatePillRow>
              <DatePill>
                <CalendarTodayIcon sx={{ fontSize: 16, color: '#6750A4' }} />
                Created: {formatDate(jdDetails.creation_time)}
              </DatePill>
              <DatePill>
                <AccessTimeIcon sx={{ fontSize: 16, color: jdDetails.is_active ? '#2D7D46' : '#B3261E' }} />
                Expires: {formatDate(jdDetails.expiration_time)}
              </DatePill>
            </DatePillRow>
          </HeaderBar>

          {/* ── Stat Cards ── */}
          <StatsRow>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{totalCandidates}</StatNum>
              <StatLbl>Total Candidates</StatLbl>
              <StatIconWrap><PeopleAltIcon /></StatIconWrap>
            </StatCard>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{newCount}</StatNum>
              <StatLbl>New</StatLbl>
              <StatIconWrap><FiberNewIcon /></StatIconWrap>
            </StatCard>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{passedCount}</StatNum>
              <StatLbl>Progressed</StatLbl>
              <StatIconWrap><CheckCircleIcon /></StatIconWrap>
            </StatCard>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{rejectedCount}</StatNum>
              <StatLbl>Not Proceeding</StatLbl>
              <StatIconWrap><GroupIcon /></StatIconWrap>
            </StatCard>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{avgScore}%</StatNum>
              <StatLbl>Avg Match Score</StatLbl>
              <StatIconWrap><AssignmentIcon /></StatIconWrap>
            </StatCard>
            <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
              <StatNum>{jdDetails.openPositions || '—'}</StatNum>
              <StatLbl>Open Positions</StatLbl>
              <StatIconWrap><WorkIcon /></StatIconWrap>
            </StatCard>
          </StatsRow>

          {/* ── Job Description text ── */}
          <DescBox>
            <CardTitle><AssignmentIcon sx={{ fontSize: 20, color: '#6750A4' }} /> Job Description</CardTitle>
            <DescText>{jdDetails.jobDescription}</DescText>
          </DescBox>

          {/* ── Info Grid ── */}
          <ContentGrid>
            {/* Left card: Role details */}
            <InfoCard>
              <CardTitle><WorkIcon sx={{ fontSize: 20, color: '#6750A4' }} /> Role Details</CardTitle>

              <InfoRow>
                <InfoKey>Job Role</InfoKey>
                <InfoVal style={{ color: '#6750A4', fontWeight: 600 }}>{jdDetails.jobRole}</InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Experience</InfoKey>
                <InfoVal>{jdDetails.experience}</InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Salary Range</InfoKey>
                <InfoVal>{jdDetails.salaryRange}</InfoVal>
              </InfoRow>
              {jdDetails.department && (
                <InfoRow>
                  <InfoKey>Department</InfoKey>
                  <InfoVal>{jdDetails.department}</InfoVal>
                </InfoRow>
              )}
              <InfoRow>
                <InfoKey>Open Positions</InfoKey>
                <InfoVal>{jdDetails.openPositions || '—'}</InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Preferred Location</InfoKey>
                <InfoVal>{jdDetails.preferredLocation || '—'}</InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Status</InfoKey>
                <InfoVal>
                  <ActiveBadge>
                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                    {jdDetails.is_active ? 'Active' : 'Inactive'}
                  </ActiveBadge>
                </InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Pass Score</InfoKey>
                <InfoVal>
                  {jdDetails.screeningTestPassPercentage !== undefined && jdDetails.screeningTestPassPercentage !== null && jdDetails.screeningTestPassPercentage !== ''
                    ? `${jdDetails.screeningTestPassPercentage}%`
                    : '—'}
                </InfoVal>
              </InfoRow>
              {jdDetails.remarks && (
                <InfoRow>
                  <InfoKey>Remarks</InfoKey>
                  <InfoVal style={{ fontSize: TYPE.small, color: '#6B5B9A' }}>{jdDetails.remarks}</InfoVal>
                </InfoRow>
              )}

              {/* Primary Skills */}
              {/* {jdDetails.skills?.length > 0 && (
                <InfoRow>
                  <InfoKey>Primary Skills</InfoKey>
                  <ChipRow>
                    {jdDetails.skills.map((s, i) => <Chip key={i}>{s}</Chip>)}
                  </ChipRow>
                </InfoRow>
              )} */}

              {/* Secondary Skills */}
              {/* {jdDetails.secondarySkills?.length > 0 && (
                <InfoRow>
                  <InfoKey>Secondary Skills</InfoKey>
                  <ChipRow>
                    {jdDetails.secondarySkills.map((s, i) => <Chip key={i} secondary={1}>{s}</Chip>)}
                  </ChipRow>
                </InfoRow>
              )} */}
            </InfoCard>

            {/* Right card: People */}
            <InfoCard>
              <CardTitle><GroupIcon sx={{ fontSize: 20, color: '#6750A4' }} /> Team & Contacts</CardTitle>

              <InfoRow>
                <InfoKey>Recruiters</InfoKey>
                <InfoVal>
                  {jdDetails.recruiterContacts?.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {jdDetails.recruiterContacts.map((r, i) => {
                        const email = typeof r === 'string' ? recruiterEmails[r] || r : r.email || "—";
                        return (
                          <ContactEmail key={i} title={email}>
                            {email}
                          </ContactEmail>
                        );
                      })}
                    </Box>
                  ) : <span style={{ color: '#B0A0D0' }}>None assigned</span>}
                </InfoVal>
              </InfoRow>

              <InfoRow>
                <InfoKey>Interviewers</InfoKey>
                <InfoVal>
                  {jdDetails.interviewerContacts?.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {jdDetails.interviewerContacts.map((iv, i) => {
                        const email = typeof iv === 'string' ? interviewerEmails[iv] || iv : iv.email || "—";
                        return (
                          <ContactEmail key={i} title={email}>
                            {email}
                          </ContactEmail>
                        );
                      })}
                    </Box>
                  ) : <span style={{ color: '#B0A0D0' }}>None assigned</span>}
                </InfoVal>
              </InfoRow>

              {/* Candidate pipeline mini-summary */}
              {totalCandidates > 0 && (
                <>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid #F0EAF9' }}>
                    <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.small, lineHeight: 1.25, color: '#2D1B5E', mb: 1.5 }}>
                      Pipeline Summary
                    </Typography>
                    {[
                      { label: 'Total', count: totalCandidates, color: '#6750A4', bg: '#EDE7F6' },
                      { label: 'New', count: newCount, color: '#E65100', bg: '#FFF3E0' },
                      { label: 'Passed', count: passedCount, color: '#2D7D46', bg: '#E6F4EA' },
                      { label: 'Rejected', count: rejectedCount, color: '#B3261E', bg: '#FDECEA' },
                    ].map(({ label, count, color, bg }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 600, color: '#49454F', width: 64, flexShrink: 0 }}>{label}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mx: 2 }}>
                          <Box sx={{ flex: 1, height: 6, borderRadius: RADIUS.pill, background: '#F0EAF9', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', borderRadius: RADIUS.pill, background: color, width: `${totalCandidates > 0 ? (count / totalCandidates) * 100 : 0}%`, transition: 'width 0.5s ease' }} />
                          </Box>
                        </Box>
                        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.caption, fontVariantNumeric: 'tabular-nums', color, background: bg, minWidth: 32, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', px: 1, borderRadius: `${RADIUS.sm}px` }}>{count}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </InfoCard>
          </ContentGrid>

          {/* ── Candidate Table ── */}
          {(userRole === 'interviewer' || userRole === 'recruiter' || userRole === 'hiringManager') && (
            <TableWrap>
              <SectionLabel>
                <PeopleAltIcon sx={{ fontSize: 20, color: '#6750A4' }} />
                Candidate Profiles
                {totalCandidates > 0 && (
                  <CountBadge>
                    {totalCandidates}
                  </CountBadge>
                )}
              </SectionLabel>

              {profilesLoading && candidateProfiles.length === 0 ? (
                <EmptyState>
                  <CircularProgress size={28} sx={{ color: '#6750A4' }} />
                  <Typography sx={emptyTextSx}>
                    Loading candidate profiles…
                  </Typography>
                </EmptyState>
              ) : profilesError ? (
                <EmptyState>
                  <Typography sx={{ ...emptyTitleSx, color: '#B3261E' }}>
                    Couldn't load candidates
                  </Typography>
                  <Typography sx={emptyTextSx}>
                    {profilesError}
                  </Typography>
                  <Button onClick={() => fetchCandidateProfiles(jdDetails._id)} sx={outlineBtnSx}>
                    Retry
                  </Button>
                </EmptyState>
              ) : paginatedProfiles.length > 0 ? (
                <TableCard>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <HeadCell>Candidate ID</HeadCell>
                          <HeadCell align="center">Status</HeadCell>
                          <HeadCell>Name</HeadCell>
                          <HeadCell>Job Role</HeadCell>
                          <HeadCell>Email</HeadCell>
                          <HeadCell>Phone</HeadCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedProfiles.map((profile) => {
                          const chip = getStatusChip(profile.overallStatus);
                          return (
                            <BodyRow key={profile._id}>
                              <BodyCell>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                  <CandidateIdBtn onClick={() => handlebuttonclick1(profile._id, profile.candidateEmail, profile.overallStatus)}>
                                    {profile.candidateID}
                                  </CandidateIdBtn>
                                  {!readOnly && profile.overallStatus === 'NewCandidate' && (
                                    <NewBadge>NEW</NewBadge>
                                  )}
                                </Box>
                              </BodyCell>
                              <BodyCell align="center">
                                <StatusChip bg={chip.bg} fg={chip.fg}>
                                  <StatusDot color={chip.dot} />
                                  {profile.overallStatus}
                                </StatusChip>
                              </BodyCell>
                              <BodyCell sx={{ fontWeight: '600 !important', maxWidth: 200, ...ELLIPSIS }} title={profile.candidatename}>{profile.candidatename}</BodyCell>
                              <BodyCell sx={{ maxWidth: 200, ...ELLIPSIS }} title={profile.jobRole}>{profile.jobRole}</BodyCell>
                              <BodyCell sx={{ fontSize: '12px !important', color: '#6B5B9A !important', maxWidth: 240, ...ELLIPSIS }} title={profile.candidateEmail}>{profile.candidateEmail}</BodyCell>
                              <BodyCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{profile.phone}</BodyCell>
                            </BodyRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {totalPages > 1 && (
                    <Pagination itemsPerPage={itemsPerPage} currentPage={currentPage} totalPages={totalPages}
                      handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} />
                  )}
                </TableCard>
              ) : (
                <EmptyState>
                  <Typography sx={{ ...emptyTitleSx, color: '#C4B0E8' }}>
                    No candidates yet
                  </Typography>
                  <Typography sx={{ ...emptyTextSx, color: '#B0A0D0' }}>
                    No candidate profiles found for this job description.
                  </Typography>
                </EmptyState>
              )}
            </TableWrap>
          )}

          {/* Dialogs */}
          <NotificationDialog open={snackbarOpen} message={snackbarMessage} onClose={() => setSnackbarOpen(false)} />

          <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}
            PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
            <DialogTitleStyled>Delete Job Description</DialogTitleStyled>
            <DialogContent>
              <DialogContentText sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5 }}>
                Are you sure you want to delete this job description? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
              <Button onClick={() => setOpenDeleteDialog(false)} sx={outlineBtnSx}>
                Cancel
              </Button>
              <Button onClick={() => handleDelete(jdDetails._id)}
                sx={{
                  fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', background: 'linear-gradient(135deg,#B71C1C,#C62828)', color: '#fff', borderRadius: `${RADIUS.sm}px`, height: 36, px: 2, ml: '0 !important',
                  '&:hover': { background: '#B71C1C' }
                }}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      {isEditJDOpen && (
        <JobDescriptionForm
          jd={selectedJD}
          isEdit={isEditJDOpen}
          hiringManagerEmail={hiringManagerEmail}
          onClose={() => { setIsEditJDOpen(false); setSelectedJD(selectedJD); }}
          onSnackbarOpen={() => { }}
          fetchJds={isEditJDOpen ? fetchJds : undefined}
        />
      )}

      {showCandidateData && (
        <CandidateData
          candidateId={selectedCandidateId}
          userRole={userRole}
          candidateEmail={candidateEmail}
          overallStatus={overallStatus}
          jdId={jdDetails._id}
          jobDescription={jdDetails.jobDescription}
          companyName={jdDetails.companyName}
          salaryRange={jdDetails.salaryRange}
          recruiterEmail={recruiterEmail}
          skills={jdDetails.skills}
          experience={jdDetails.experience}
          readOnly={readOnly}
          onBack={handleClose}
        />
      )}
    </PageWrapper>
  );
};

// Standalone /JDDetails route renders outside RecruiterDashboard — bring the theme along.
const JDDetails = (props) => (
  <ThemeProvider theme={recruiterTheme}>
    <JDDetailsContent {...props} />
  </ThemeProvider>
);

export default JDDetails;